import React, { useState } from 'react';
import { format } from 'date-fns';
import { comprehensiveHRService } from '../../../lib/checkInAttendanceService';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const HRNotifications = ({ notifications, loading, onRefresh, staffId }) => {
  const [filter, setFilter] = React.useState('all');

  const handleMarkRead = async (notifId) => {
    const { error } = await comprehensiveHRService?.markNotificationRead(notifId);
    if (!error) {
      onRefresh?.();
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'shift_reminder':
        return 'Calendar';
      case 'missed_clock_in':
        return 'AlertCircle';
      case 'overtime_warning':
        return 'Clock';
      case 'certification_expiry':
        return 'Award';
      case 'payroll_ready':
        return 'DollarSign';
      case 'leave_approved':
        return 'CheckCircle';
      case 'leave_rejected':
        return 'XCircle';
      default:
        return 'Bell';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent':
        return 'bg-error/10 text-error border-error/20';
      case 'high':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'medium':
        return 'bg-primary/10 text-primary border-primary/20';
      case 'low':
        return 'bg-muted text-muted-foreground border-border';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const filteredNotifications = notifications?.filter((notif) => {
    if (filter === 'unread') return !notif?.is_read;
    if (filter === 'read') return notif?.is_read;
    return true;
  });

  const unreadCount = notifications?.filter((n) => !n?.is_read)?.length || 0;

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-primary/10">
              <Icon name="Bell" size={24} className="text-primary" />
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">{notifications?.length}</div>
              <div className="text-xs text-muted-foreground">Total Notifications</div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-warning/10">
              <Icon name="AlertCircle" size={24} className="text-warning" />
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">{unreadCount}</div>
              <div className="text-xs text-muted-foreground">Unread</div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-error/10">
              <Icon name="AlertTriangle" size={24} className="text-error" />
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">
                {notifications?.filter((n) => n?.priority === 'urgent')?.length || 0}
              </div>
              <div className="text-xs text-muted-foreground">Urgent</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h2 className="text-xl font-semibold text-foreground">Notifications</h2>
            <div className="flex gap-2">
              <button
                onClick={() => setFilter('all')}
                className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                  filter === 'all' ?'bg-primary text-white' :'bg-muted text-muted-foreground hover:bg-muted/70'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setFilter('unread')}
                className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                  filter === 'unread' ?'bg-primary text-white' :'bg-muted text-muted-foreground hover:bg-muted/70'
                }`}
              >
                Unread ({unreadCount})
              </button>
              <button
                onClick={() => setFilter('read')}
                className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                  filter === 'read' ?'bg-primary text-white' :'bg-muted text-muted-foreground hover:bg-muted/70'
                }`}
              >
                Read
              </button>
            </div>
          </div>
          <Button onClick={onRefresh} variant="outline" disabled={loading}>
            <Icon name={loading ? 'Loader2' : 'RefreshCw'} size={18} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>
      </div>

      {/* Notifications List */}
      {loading && notifications?.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Icon name="Loader2" size={32} className="animate-spin text-primary" />
        </div>
      ) : filteredNotifications?.length === 0 ? (
        <div className="card text-center py-12">
          <Icon name="Bell" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Notifications</h3>
          <p className="text-muted-foreground">
            {filter === 'unread' ? 'You have no unread notifications' : 'No notifications to display'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredNotifications?.map((notif) => (
            <div
              key={notif?.id}
              className={`card border ${
                !notif?.is_read ? 'bg-primary/5 border-primary/20' : 'border-border'
              }`}
            >
              <div className="flex items-start gap-4">
                <div
                  className={`p-3 rounded-lg border ${
                    getPriorityColor(notif?.priority)
                  }`}
                >
                  <Icon name={getNotificationIcon(notif?.notification_type)} size={24} />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h3 className="font-semibold text-foreground mb-1">{notif?.title}</h3>
                      <p className="text-sm text-muted-foreground">{notif?.message}</p>
                    </div>
                    {!notif?.is_read && (
                      <Button size="sm" variant="outline" onClick={() => handleMarkRead(notif?.id)}>
                        <Icon name="Check" size={14} />
                      </Button>
                    )}
                  </div>

                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span className="capitalize">{notif?.notification_type?.replace('_', ' ')}</span>
                    <span>•</span>
                    <span className="capitalize">{notif?.priority} priority</span>
                    <span>•</span>
                    <span>{format(new Date(notif?.created_at), 'MMM d, yyyy h:mm a')}</span>
                    {notif?.is_read && (
                      <>
                        <span>•</span>
                        <span className="flex items-center gap-1">
                          <Icon name="CheckCircle" size={12} className="text-success" />
                          Read
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default HRNotifications;